import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNavbar from '../../components/AdminNavbar';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import { 
    fetchSingleCatalog, fetchAdminProducts, deleteProduct, 
    setProductCover, deleteProductImage 
} from '../../store/slices/adminSlice';
import { getCurrencySymbol } from '../../helper/currencyHelper';
import { getAllProductImages, getProductCoverUrl } from '../../helper/imageHelper';

const STOCK_STATUSES = [
    { value: 'available', label: 'Disponible', color: 'success' },
    { value: 'ondemand', label: 'Bajo Pedido', color: 'info' },
    { value: 'ask', label: 'Consultar', color: 'warning' },
    { value: 'transit', label: 'En Tránsito', color: 'primary' },
    { value: 'soldout', label: 'Agotado', color: 'danger' }
];

const ProductDetailView = () => {
    const { catalogID, productID } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { 
        authUser, catalogs, products, productsLoading 
    } = useSelector((state) => state.admin);

    const catalog = catalogs.find(c => c.id === catalogID);
    const product = products.find(p => p.id === productID);

    const [selectedImageId, setSelectedImageId] = useState(null);

    useEffect(() => {
        if (authUser?.uid && catalogID) {
            if (!catalog) {
                dispatch(fetchSingleCatalog({ userID: authUser.uid, catalogID }));
            }
            dispatch(fetchAdminProducts({ userID: authUser.uid, catalogID }));
        }
    }, [dispatch, authUser, catalogID, catalog]);

    const currencySymbol = catalog ? getCurrencySymbol(catalog) : '$';

    const pictureEntries = React.useMemo(() => {
        return getAllProductImages(product);
    }, [product]);

    // Determine current active main picture URL
    const activeImageItem = selectedImageId 
        ? pictureEntries.find(img => img.id === selectedImageId)
        : (pictureEntries.find(img => img.isCover || img.id === product?.coverRef) || pictureEntries[0]);

    const activeImageUrl = activeImageItem?.url || getProductCoverUrl(product);

    const handleSetCover = async (imageID) => {
        await dispatch(setProductCover({
            userID: authUser.uid,
            catalogID,
            productID,
            imageID
        }));
    };

    const handleDeleteImage = async (imageID) => {
        if (window.confirm("¿Eliminar esta imagen del producto?")) {
            await dispatch(deleteProductImage({
                userID: authUser.uid,
                catalogID,
                productID,
                imageID
            }));
            if (selectedImageId === imageID) {
                setSelectedImageId(null);
            }
        }
    };

    const handleDeleteProduct = async () => {
        if (window.confirm("¿Estás seguro de eliminar este producto completamente?")) {
            await dispatch(deleteProduct({
                userID: authUser.uid,
                catalogID,
                productID
            }));
            navigate(`/admin/dashboard/${catalogID}`);
        }
    };

    if (productsLoading || !product) {
        return (
            <div className="bg-gray-50 min-vh-100 font-sans pb-60">
                <AdminNavbar />
                <AdminBreadcrumb catalog={catalog} />
                <div className="container container-lg text-center py-60">
                    <div className="spinner-border text-main-600 mb-12" role="status" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                    <p className="text-gray-500 text-xs mb-0">Cargando detalles del producto...</p>
                </div>
            </div>
        );
    }

    const stockObj = STOCK_STATUSES.find(s => s.value === product.stock) || STOCK_STATUSES[0];
    const originalPrice = parseFloat(product.price) || 0;
    const discountPct = parseFloat(product.discount) || 0;
    const finalPrice = discountPct > 0 ? (originalPrice * (1 - discountPct / 100)).toFixed(2) : originalPrice.toFixed(2);

    return (
        <div className="bg-gray-50 min-vh-100 font-sans pb-60">
            <AdminNavbar />
            <AdminBreadcrumb catalog={catalog} product={product} />

            <div className="container container-lg">
                <div className="row g-24">
                    {/* Left Column: Image Gallery */}
                    <div className="col-lg-6">
                        <div className="bg-white p-20 rounded-24 border shadow-2xs">
                            {/* Main Preview Box */}
                            <div className="w-100 rounded-16 border overflow-hidden position-relative bg-gray-50 flex-center mb-16" style={{ height: '360px' }}>
                                <img 
                                    src={activeImageUrl} 
                                    alt={product.name} 
                                    className="w-100 h-100 object-fit-contain" 
                                />

                                {activeImageItem && (activeImageItem.isCover || activeImageItem.id === product.coverRef) && (
                                    <span className="position-absolute top-12 start-12 badge bg-main-600 text-white rounded-pill px-12 py-6 text-xs fw-bold shadow-xs">
                                        Portada Principal
                                    </span>
                                )}
                            </div>

                            {/* Image Thumbnails Carousel */}
                            {pictureEntries.length > 0 ? (
                                <div>
                                    <label className="form-label text-3xs fw-bold text-gray-500 text-uppercase mb-8">
                                        Galería de Fotos ({pictureEntries.length})
                                    </label>
                                    <div className="d-flex flex-wrap gap-12">
                                        {pictureEntries.map(img => {
                                            const isSelected = activeImageItem?.id === img.id;
                                            const isCover = img.isCover || product.coverRef === img.id;

                                            return (
                                                <div 
                                                    key={img.id} 
                                                    className={`position-relative rounded-12 border overflow-hidden cursor-pointer transition-2 ${
                                                        isSelected ? 'ring-2 ring-main-600 border-main-600' : 'border-gray-200 opacity-80 hover-opacity-100'
                                                    }`}
                                                    style={{ width: '80px', height: '80px' }}
                                                    onClick={() => setSelectedImageId(img.id)}
                                                >
                                                    <img src={img.url} alt="Thumbnail" className="w-100 h-100 object-fit-cover" />

                                                    {isCover && (
                                                        <span className="position-absolute top-0 start-0 bg-main-600 text-white text-3xs px-4 py-1 fw-bold">
                                                            Portada
                                                        </span>
                                                    )}

                                                    <div className="position-absolute bottom-0 start-0 end-0 bg-black-50 p-2 flex-between">
                                                        {!isCover && (
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-xs btn-light text-3xs p-2 text-main-600 fw-bold"
                                                                onClick={(e) => { e.stopPropagation(); handleSetCover(img.id); }}
                                                                title="Establecer portada"
                                                            >
                                                                Portada
                                                            </button>
                                                        )}
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-xs btn-danger text-3xs p-2 ms-auto"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                                                            title="Eliminar imagen"
                                                        >
                                                            <i className="ph ph-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-400 text-xs mb-0 text-center py-12">El producto no tiene imágenes registradas.</p>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Product Details Info */}
                    <div className="col-lg-6">
                        <div className="bg-white p-24 rounded-24 border shadow-2xs h-100 d-flex flex-column">
                            {/* Stock Badge & Catalog Name */}
                            <div className="d-flex align-items-center justify-content-between mb-12 flex-wrap gap-8">
                                <span className={`badge bg-${stockObj.color}-100 text-${stockObj.color}-700 rounded-pill text-xs px-12 py-6 fw-bold`}>
                                    {stockObj.label}
                                </span>

                                <span className="text-2xs text-gray-500 flex-align gap-6">
                                    Catálogo: <strong className="text-gray-900">{catalog?.name}</strong>
                                </span>
                            </div>

                            {/* Product Name */}
                            <h3 className="fw-bold text-gray-900 mb-16">{product.name}</h3>

                            {/* Pricing Section */}
                            <div className="bg-gray-50 p-16 rounded-16 border mb-20">
                                <span className="text-2xs text-gray-400 text-uppercase fw-bold d-block mb-4">Precio</span>
                                <div className="d-flex align-items-baseline gap-12 flex-wrap">
                                    <h2 className="fw-bold text-main-600 mb-0">
                                        {currencySymbol}{finalPrice}
                                    </h2>

                                    {discountPct > 0 && (
                                        <>
                                            <span className="text-gray-400 text-md text-decoration-line-through">
                                                {currencySymbol}{originalPrice.toFixed(2)}
                                            </span>
                                            <span className="badge bg-danger-100 text-danger-700 rounded-pill text-xs px-10 py-4 fw-bold">
                                                -{discountPct}% Descuento
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Description Section */}
                            <div className="mb-24 flex-grow-1">
                                <h6 className="fw-bold text-gray-900 text-xs text-uppercase mb-8">Descripción Detallada</h6>
                                <div className="p-16 bg-gray-50 rounded-16 border text-gray-700 text-xs leading-relaxed whitespace-pre-line">
                                    {product.description || 'Sin descripción ingresada para este producto.'}
                                </div>
                            </div>

                            {/* Bottom Actions Bar */}
                            <div className="pt-20 border-top d-flex align-items-center justify-content-between flex-wrap gap-12 mt-auto">
                                <button 
                                    type="button" 
                                    className="btn btn-outline-gray-200 btn-sm rounded-pill px-18 py-8 text-xs fw-semibold text-gray-700 flex-align gap-6"
                                    onClick={() => navigate(`/admin/dashboard/${catalogID}`)}
                                >
                                    <i className="ph ph-arrow-left text-sm"></i>
                                    Volver al Catálogo
                                </button>

                                <div className="d-flex align-items-center gap-8">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-danger btn-sm rounded-pill px-16 py-8 text-xs fw-semibold flex-align gap-6"
                                        onClick={handleDeleteProduct}
                                    >
                                        <i className="ph ph-trash text-sm"></i>
                                        Eliminar
                                    </button>

                                    <button 
                                        type="button" 
                                        className="btn btn-main btn-sm rounded-pill px-20 py-8 text-xs fw-bold flex-align gap-6 shadow-xs"
                                        onClick={() => navigate(`/admin/dashboard/${catalogID}/${productID}/edit`)}
                                    >
                                        <i className="ph ph-pencil-simple text-sm"></i>
                                        Editar Producto
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailView;
